/** Motion Reconstruction — Canonical Generation Card
 * (VISTOERA_MOTION_RECONSTRUCTION_CANONICAL_GENERATION_CARD_RESULT_FIRST_EXTERNAL_QUICK_ACTIONS_V2)
 *
 * Vertical order (HARD): Header → Result → Input Assets → Parameters → Prompt → Footer.
 * Collapsed: Header + Result only. External Quick Actions = CARD RIGHT OUTSIDE
 * (`MotionResultWorkflowRail`) on Result hover with hover-bridge.
 *
 * Backend motion extract / reconstruct workers / control plane unchanged.
 */

import {
  CloseOutlined,
  CopyOutlined,
  HistoryOutlined,
  LoadingOutlined,
  ThunderboltOutlined,
  UserSwitchOutlined,
  VideoCameraOutlined,
} from '@ant-design/icons'
import { Handle, Position, useUpdateNodeInternals, type NodeProps } from '@xyflow/react'
import { Tooltip, message } from 'antd'
import CanvasSelect from '../CanvasSelect'
import { memo, useCallback, useEffect, useMemo, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react'
import { useTranslation } from 'react-i18next'
import { useCanvasGenerationI18n } from '../useCanvasGenerationI18n'
import {
  getMotionReconstruction,
  selectMotionReconstructionTrack,
  submitMotionReconstruction,
  videoAssetPlaybackUrl,
  type SaasApiError,
} from '../../../../api'
import { videoAssetItemFromBinding } from '../canvasVideoAssetResolve'
import { patchCanvasFieldsIfChanged } from '../canvasFieldsEqual'
import MotionSkeletonPreview from '../components/MotionSkeletonPreview'
import MotionTrackSelectionPanel from '../components/MotionTrackSelectionPanel'
import CanvasActionPopover from '../CanvasActionPopover'
import { useNodeDefinition } from '../nodeRegistry'
import { useCanvasNodeRegistryI18n } from '../nodeRegistryI18n'
import { loadMotionPreviewDeduped, type MotionPreviewPayload } from '../motionAssetPreview'
import {
  buildMotionReconstructionPayload,
  isReconstructionBusy,
  mapReconstructionApiStatus,
  motionReconstructionResultFingerprint,
  normalizeMotionQualityMode,
  shouldPollReconstructionStatus,
  type MotionReconstructionStatus,
  type MotionTrackSummary,
} from '../motionReconstructionProvider'
import {
  localizeMotionReconstructionError,
  logMotionReconstructionErrorDev,
  resolveMotionReconstructionFailedDisplay,
} from '../motionReconstructionErrors'
import { functionCardShowsInspector } from '../functionCardInteractionV2'
import { NODE_EXPAND_TRIGGER_CLASS, canvasNodeShellStateClass } from '../canvasNodeShellV2'
import CanvasFunctionCardHeader from '../CanvasFunctionCardHeader'
import MotionResultWorkflowRail from './MotionResultWorkflowRail'
import type { FlowNodeData } from '../types'

const HEADER_HEIGHT = 52
import { generationNodeCardWidth } from '../generationNodeWidthPresets'

const NODE_CARD_WIDTH = generationNodeCardWidth('motion_reconstruction')
const RESULT_MIN_HEIGHT = 260
const RESULT_MAX_HEIGHT = 420
const MOTION_SKILL_KEY = 'motion_reconstruction'
const MOTION_SKILL_VERSION = 'v1'
const PROMPT_MAX_LENGTH = 500

type ConnectedVideo = {
  videoAssetId: string
  label: string
  ready: boolean
}

function applyReconstructionResponse(
  data: {
    reconstruction_id?: string | null
    status: string
    output_motion_asset_id?: string | null
    input_motion_asset_id?: string | null
    error_code?: string | null
    error_message?: string | null
    job_id?: string | null
    available_tracks?: MotionTrackSummary[]
    selected_track_id?: string | null
  },
  patchFields: (patch: Record<string, unknown>) => void,
  extraPatch?: Record<string, unknown>,
) {
  const mapped = mapReconstructionApiStatus(data.status)
  patchFields({
    ...(extraPatch || {}),
    ...(data.reconstruction_id ? { reconstructionId: data.reconstruction_id } : {}),
    status: mapped,
    outputMotionAssetId: data.output_motion_asset_id,
    inputMotionAssetId: data.input_motion_asset_id,
    errorCode: mapped === 'FAILED' ? data.error_code || 'RECONSTRUCTION_FAILED' : null,
    errorMessage: null,
    jobId: data.job_id,
    availableTracks: data.available_tracks || [],
    selectedTrackId: data.selected_track_id || null,
  })
  if (mapped === 'FAILED') {
    logMotionReconstructionErrorDev(data.error_code, data.error_message)
  }
  return mapped
}

function MotionReconstructionNodeImpl({ id, data, selected }: NodeProps) {
  const { t } = useTranslation('canvas')
  const { mediaHeader } = useCanvasGenerationI18n()
  const { nodeLabel } = useCanvasNodeRegistryI18n()
  const nodeData = data as unknown as FlowNodeData & {
    workspaceId?: string
    projectId?: string
    siteId?: string
    connectedVideo?: ConnectedVideo | null
    onOpenVideoAssetPicker?: FlowNodeData['onOpenVideoAssetPicker']
    onQuickCreateGenerateVideoFromMotion?: FlowNodeData['onQuickCreateGenerateVideoFromMotion']
  }
  const def = useNodeDefinition(nodeData.nodeType)
  const displayLabel = nodeLabel(def.nodeType, def.label)
  const workspaceId = nodeData.workspaceId || ''
  const projectId = nodeData.projectId || ''
  const fields = nodeData.fields || {}
  const status = ((fields.status as string | undefined) ?? 'WAITING') as MotionReconstructionStatus
  const reconstructionId = (fields.reconstructionId as string | undefined) ?? null
  const outputMotionAssetId = (fields.outputMotionAssetId as string | undefined) ?? null
  const previousOutputMotionAssetId = (fields.previousOutputMotionAssetId as string | undefined) ?? null
  const inputMotionAssetId = (fields.inputMotionAssetId as string | undefined) ?? null
  const localVideoAssetId = (fields.inputVideoAssetId as string | undefined) ?? null
  const errorCode = (fields.errorCode as string | null | undefined) ?? null
  const legacyErrorMessage = (fields.errorMessage as string | null | undefined) ?? null
  const availableTracks = (fields.availableTracks as MotionTrackSummary[] | undefined) ?? []
  const selectedTrackId = (fields.selectedTrackId as string | null | undefined) ?? null
  const qualityMode = normalizeMotionQualityMode(fields.qualityMode)
  const prompt = ((fields.prompt as string | undefined) ?? '').slice(0, PROMPT_MAX_LENGTH)
  const skillSelectionSource = (fields.skillSelectionSource as string) || 'AUTO'
  const needsRegeneration = Boolean(fields.needsRegeneration)
  const generatedFromFingerprint = (fields.generatedFromFingerprint as string | undefined) ?? null
  const connectedVideo = nodeData.connectedVideo

  const inputVideoAssetId = connectedVideo?.ready ? connectedVideo.videoAssetId : localVideoAssetId

  const [resultPreview, setResultPreview] = useState<MotionPreviewPayload | null>(null)
  const [videoAlignedPreview, setVideoAlignedPreview] = useState<MotionPreviewPayload | null>(null)
  const [playing, setPlaying] = useState(false)
  const [frameIdx, setFrameIdx] = useState(0)
  const [trackSubmitting, setTrackSubmitting] = useState(false)
  const [historyOpen, setHistoryOpen] = useState(false)
  const [historyBtnEl, setHistoryBtnEl] = useState<HTMLElement | null>(null)
  const [resultZoneActive, setResultZoneActive] = useState(false)
  const resultZoneLeaveTimerRef = useRef<number | null>(null)
  const submittingRef = useRef(false)
  const trackSubmittingRef = useRef(false)
  const pollTimerRef = useRef<number | null>(null)
  const statusBeforeSubmitRef = useRef<MotionReconstructionStatus>('READY')
  const updateNodeInternals = useUpdateNodeInternals()

  const isBusy = isReconstructionBusy(status)
  const needsSelection = status === 'SELECTION_REQUIRED' && availableTracks.length > 1
  const showResultPreview = Boolean(outputMotionAssetId) && status !== 'WAITING'
  const canGenerate = Boolean(inputVideoAssetId && !isBusy && !needsSelection)

  const isDetailExpanded = Boolean(nodeData.isDetailExpanded)
  const showExpanded = functionCardShowsInspector(isDetailExpanded, nodeData.isDragTempCollapsed)
  const cardWidth = NODE_CARD_WIDTH
  const cardWidthPx = NODE_CARD_WIDTH
  const resultHeight = Math.min(
    RESULT_MAX_HEIGHT,
    Math.max(RESULT_MIN_HEIGHT, Math.round((cardWidthPx - 28) * (9 / 16))),
  )

  const activateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) {
      window.clearTimeout(resultZoneLeaveTimerRef.current)
      resultZoneLeaveTimerRef.current = null
    }
    setResultZoneActive(true)
  }, [])
  const deactivateResultZone = useCallback(() => {
    if (resultZoneLeaveTimerRef.current != null) window.clearTimeout(resultZoneLeaveTimerRef.current)
    resultZoneLeaveTimerRef.current = window.setTimeout(() => setResultZoneActive(false), 160)
  }, [])

  useEffect(() => {
    if (!nodeData.playbackResetKey) return
    setPlaying(false)
    setFrameIdx(0)
  }, [nodeData.playbackResetKey])

  const videoPlaybackUrl = useMemo(() => {
    if (!workspaceId || !inputVideoAssetId) return null
    const entityId = inputVideoAssetId.includes(':') ? inputVideoAssetId : `upload:${inputVideoAssetId}`
    return videoAssetPlaybackUrl(workspaceId, videoAssetItemFromBinding(entityId, null))
  }, [inputVideoAssetId, workspaceId])

  const outputHandleTop = (HEADER_HEIGHT + resultHeight) / 2
  const videoHandleTop = outputHandleTop

  const statusLabel = useMemo(() => {
    const key = `motionReconstruction.status.${status.toLowerCase()}`
    return t(key, { defaultValue: status })
  }, [status, t])

  const resultStatusBadge = useMemo(() => {
    if (needsSelection) return t('motionReconstruction.status.selection_required')
    if (isBusy) return t('generation.generating', { defaultValue: '生成中' })
    if (needsRegeneration && showResultPreview) return t('generation.needsRegeneration', { defaultValue: '需要重新生成' })
    if (status === 'FAILED' && !showResultPreview) return t('motionReconstruction.status.failed')
    if (showResultPreview) return t('generation.resultReady', { defaultValue: '已生成' })
    return t('generation.resultEmpty', { defaultValue: '未生成' })
  }, [isBusy, needsRegeneration, needsSelection, showResultPreview, status, t])

  const failedDisplayMessage = useMemo(() => {
    if (status !== 'FAILED') return null
    return resolveMotionReconstructionFailedDisplay({ errorCode, errorMessage: legacyErrorMessage }, t)
  }, [errorCode, legacyErrorMessage, status, t])

  const currentFingerprint = useMemo(
    () =>
      motionReconstructionResultFingerprint({
        inputVideoAssetId,
        selectedTrackId,
        qualityMode,
        prompt,
      }),
    [inputVideoAssetId, prompt, qualityMode, selectedTrackId],
  )

  const patchFields = useCallback(
    (patch: Record<string, unknown>) => {
      const next = patchCanvasFieldsIfChanged(fields as Record<string, unknown>, patch)
      if (!next) return
      nodeData.onChangeFields?.(id, next)
    },
    [fields, id, nodeData],
  )

  const markStaleIfHasResult = useCallback(
    (patch: Record<string, unknown>) => {
      const nextFp = motionReconstructionResultFingerprint({
        inputVideoAssetId: (patch.inputVideoAssetId as string | undefined) ?? inputVideoAssetId,
        selectedTrackId: (patch.selectedTrackId as string | null | undefined) ?? selectedTrackId,
        qualityMode: (patch.qualityMode as string | undefined) ?? qualityMode,
        prompt: (patch.prompt as string | undefined) ?? prompt,
      })
      const shouldStale =
        Boolean(outputMotionAssetId) &&
        (status === 'COMPLETED' || needsRegeneration) &&
        generatedFromFingerprint != null &&
        nextFp !== generatedFromFingerprint
      return shouldStale ? { ...patch, needsRegeneration: true } : patch
    },
    [
      generatedFromFingerprint,
      inputVideoAssetId,
      needsRegeneration,
      outputMotionAssetId,
      prompt,
      qualityMode,
      selectedTrackId,
      status,
    ],
  )

  const clearPoll = useCallback(() => {
    if (pollTimerRef.current != null) {
      window.clearTimeout(pollTimerRef.current)
      pollTimerRef.current = null
    }
  }, [])

  const pollReconstruction = useCallback(
    (rid: string) => {
      clearPoll()
      const tick = async () => {
        try {
          const res = await getMotionReconstruction(workspaceId, rid)
          const mapped = applyReconstructionResponse(res.data, patchFields)
          if (mapped === 'COMPLETED' || mapped === 'FAILED') {
            if (mapped === 'FAILED') {
              const display = localizeMotionReconstructionError(
                { code: res.data.error_code, message: res.data.error_message },
                t,
              )
              message.error(display)
            }
            return
          }
          pollTimerRef.current = window.setTimeout(tick, mapped === 'SELECTION_REQUIRED' ? 8000 : 4000)
        } catch {
          pollTimerRef.current = window.setTimeout(tick, 6000)
        }
      }
      void tick()
    },
    [clearPoll, patchFields, t, workspaceId],
  )

  const hydrateReconstruction = useCallback(
    async (rid: string) => {
      try {
        const res = await getMotionReconstruction(workspaceId, rid)
        const mapped = applyReconstructionResponse(res.data, patchFields)
        if (shouldPollReconstructionStatus(mapped)) {
          pollReconstruction(rid)
        }
      } catch {
        /* ignore — node fields remain last persisted state */
      }
    },
    [patchFields, pollReconstruction, workspaceId],
  )

  useEffect(() => () => clearPoll(), [clearPoll])

  useEffect(() => {
    if (reconstructionId && shouldPollReconstructionStatus(status)) {
      pollReconstruction(reconstructionId)
    }
  }, [pollReconstruction, reconstructionId, status])

  useEffect(() => {
    if (reconstructionId || !isReconstructionBusy(status) || submittingRef.current) return
    patchFields({ status: 'READY', errorCode: null, errorMessage: null })
  }, [patchFields, reconstructionId, status])

  useEffect(() => {
    if (!reconstructionId || !workspaceId) return
    if (status === 'COMPLETED' || status === 'FAILED') return
    void hydrateReconstruction(reconstructionId)
  }, [hydrateReconstruction, reconstructionId, status, workspaceId])

  useEffect(() => {
    setResultPreview(null)
    setFrameIdx(0)
    setPlaying(false)
    if (!workspaceId || !outputMotionAssetId || !showResultPreview) return
    let cancelled = false
    loadMotionPreviewDeduped(workspaceId, outputMotionAssetId)
      .catch(() => new Promise((resolve) => setTimeout(resolve, 1200)).then(() => loadMotionPreviewDeduped(workspaceId, outputMotionAssetId)))
      .then((p) => {
        if (!cancelled) setResultPreview(p)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [outputMotionAssetId, showResultPreview, workspaceId])

  useEffect(() => {
    setVideoAlignedPreview(null)
    if (!workspaceId || !inputMotionAssetId || !showResultPreview) return
    let cancelled = false
    loadMotionPreviewDeduped(workspaceId, inputMotionAssetId)
      .catch(() => new Promise((resolve) => setTimeout(resolve, 1200)).then(() => loadMotionPreviewDeduped(workspaceId, inputMotionAssetId)))
      .then((p) => {
        if (!cancelled) setVideoAlignedPreview(p)
      })
      .catch(() => {})
    return () => {
      cancelled = true
    }
  }, [inputMotionAssetId, showResultPreview, workspaceId])

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => updateNodeInternals(id))
    return () => window.cancelAnimationFrame(frame)
  }, [id, needsSelection, resultHeight, showExpanded, showResultPreview, updateNodeInternals])

  const openVideoPicker = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!nodeData.onOpenVideoAssetPicker) return
    nodeData.onOpenVideoAssetPicker({
      nodeId: id,
      title: t('motionReconstruction.video.select'),
      initialSelection: localVideoAssetId,
    })
  }

  const runGenerate = useCallback(async () => {
    if (!workspaceId || !projectId || !inputVideoAssetId || submittingRef.current) return
    submittingRef.current = true
    statusBeforeSubmitRef.current = status
    clearPoll()
    const historyPatch =
      status === 'COMPLETED' && outputMotionAssetId
        ? { previousOutputMotionAssetId: outputMotionAssetId }
        : {}
    patchFields({
      status: 'SUBMITTING',
      errorCode: null,
      errorMessage: null,
      needsRegeneration: false,
      ...historyPatch,
    })
    try {
      const payload = buildMotionReconstructionPayload(
        inputVideoAssetId,
        qualityMode,
        selectedTrackId,
      )
      const res = await submitMotionReconstruction(workspaceId, projectId, id, payload)
      applyReconstructionResponse(res.data, patchFields, {
        inputVideoAssetId,
        generatedFromFingerprint: currentFingerprint,
        needsRegeneration: false,
        prompt,
        qualityMode,
        skillKey: skillSelectionSource === 'EXPLICIT' ? MOTION_SKILL_KEY : null,
        skillVersion: MOTION_SKILL_VERSION,
        skillSelectionSource,
      })
      if (res.data.reconstruction_id) pollReconstruction(res.data.reconstruction_id)
    } catch (err) {
      const apiErr = err as SaasApiError
      const display = localizeMotionReconstructionError(apiErr, t)
      message.error(display)
      const isActiveLock = apiErr.code === 'motion_reconstruction_already_active' || apiErr.status === 409
      if (isActiveLock && reconstructionId) {
        void hydrateReconstruction(reconstructionId)
      } else if (!isActiveLock) {
        const hadResult = Boolean(outputMotionAssetId || previousOutputMotionAssetId)
        patchFields({
          status: hadResult ? 'COMPLETED' : statusBeforeSubmitRef.current === 'SUBMITTING' ? 'READY' : statusBeforeSubmitRef.current,
          needsRegeneration: hadResult,
          errorCode: null,
          errorMessage: null,
        })
      }
    } finally {
      submittingRef.current = false
    }
  }, [
    clearPoll,
    currentFingerprint,
    hydrateReconstruction,
    id,
    inputVideoAssetId,
    outputMotionAssetId,
    patchFields,
    pollReconstruction,
    previousOutputMotionAssetId,
    projectId,
    prompt,
    qualityMode,
    reconstructionId,
    selectedTrackId,
    skillSelectionSource,
    status,
    t,
    workspaceId,
  ])

  const runTrackSelection = useCallback(
    async (trackId: string) => {
      if (!workspaceId || !reconstructionId || trackSubmittingRef.current) return
      trackSubmittingRef.current = true
      setTrackSubmitting(true)
      try {
        const res = await selectMotionReconstructionTrack(workspaceId, reconstructionId, {
          selected_track_id: trackId,
        })
        applyReconstructionResponse(res.data, patchFields, { selectedTrackId: trackId })
        pollReconstruction(reconstructionId)
      } catch (err) {
        const apiErr = err as SaasApiError
        message.error(localizeMotionReconstructionError(apiErr, t))
      } finally {
        trackSubmittingRef.current = false
        setTrackSubmitting(false)
      }
    },
    [patchFields, pollReconstruction, reconstructionId, t, workspaceId],
  )

  const handleGenerateClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    if (!canGenerate) return
    void runGenerate()
  }

  const handleCopyClick = (e: ReactMouseEvent) => {
    e.stopPropagation()
    nodeData.onDuplicateNode?.(id)
  }
  const handleDeleteClick = (e: ReactMouseEvent<HTMLButtonElement>) => {
    e.stopPropagation()
    nodeData.onRequestDelete?.(id, e.currentTarget)
  }
  const handleResultExpandClick = () => {
    nodeData.onExpandFunctionCardDetail?.(id)
  }

  const videoLabel =
    connectedVideo?.ready
      ? connectedVideo.label
      : (fields.inputVideoLabel as string | undefined) ||
        (inputVideoAssetId ? t('motionReconstruction.video.connected') : t('motionReconstruction.video.waiting'))

  const showExternalQuickActions = Boolean(showResultPreview && status === 'COMPLETED' && outputMotionAssetId)
  const previewWidth = Math.max(280, cardWidthPx - 28)

  return (
    <div
      className={`pcv-node pcv-node--media pcv-node--motion-processor pcv-node--motion-reconstruction${selected ? ' is-selected' : ''} ${canvasNodeShellStateClass(showExpanded)}${needsSelection ? ' pcv-node--motion-selection' : ''}${resultZoneActive ? ' is-result-zone-active' : ''}`}
      style={{ width: cardWidth }}
      data-testid="canvas-motion-reconstruction-node"
      data-expanded={showExpanded ? 'yes' : 'no'}
      data-canonical-order="Header|Result|InputAssets|Parameters|Prompt|Footer"
      data-prompt-after-parameters="yes"
      data-input-mode="video-only"
      data-diverge-supported="no"
    >
      <Handle
        type="target"
        position={Position.Left}
        id="video-in"
        className="pcv-handle pcv-handle--lg"
        style={{ top: videoHandleTop }}
      />
      <Handle
        type="source"
        position={Position.Right}
        id="motion-out"
        className="pcv-handle pcv-handle--lg"
        style={{ top: outputHandleTop }}
      />

      <div className="pcv-node-inner">
        {/* ① Header */}
        <CanvasFunctionCardHeader
          icon={def.icon}
          color={def.color}
          title={displayLabel}
          actions={
            <>
              <Tooltip title={t('generation.historyVersions', { defaultValue: '历史版本' })} mouseEnterDelay={0.3}>
                <button
                  type="button"
                  ref={setHistoryBtnEl}
                  className="pcv-media-hover-btn"
                  data-testid="motion-history-btn"
                  aria-label={t('generation.historyVersions', { defaultValue: '历史版本' })}
                  onClick={(e) => {
                    e.stopPropagation()
                    setHistoryBtnEl(e.currentTarget)
                    setHistoryOpen(true)
                  }}
                >
                  <HistoryOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.duplicate} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn" aria-label={mediaHeader.duplicate} onClick={handleCopyClick}>
                  <CopyOutlined />
                </button>
              </Tooltip>
              <Tooltip title={mediaHeader.delete} mouseEnterDelay={0.3}>
                <button type="button" className="pcv-media-hover-btn pcv-media-hover-btn--danger" aria-label={mediaHeader.delete} onClick={handleDeleteClick}>
                  <CloseOutlined />
                </button>
              </Tooltip>
            </>
          }
        />

        {/* ② Result — always visible */}
        <div
          className={`pcv-gen-result-area pcv-gen-motion-result-area ${NODE_EXPAND_TRIGGER_CLASS}`}
          data-testid="motion-result-area"
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
          onClick={handleResultExpandClick}
        >
          <div className="pcv-gen-result-section-label">
            <span>{t('motionReconstruction.result.section', { defaultValue: '生成结果' })}</span>
            <span
              className={`pcv-gen-result-status-badge${showResultPreview && !isBusy && !needsRegeneration ? ' is-ready' : ''}${isBusy || needsSelection ? ' is-busy' : ''}${needsRegeneration && showResultPreview && !isBusy ? ' is-stale' : ''}`}
              data-testid="motion-result-status-badge"
            >
              {resultStatusBadge}
            </span>
          </div>

          <div className="pcv-motion-result-frame" style={{ height: resultHeight }} data-testid="motion-result-frame">
            {needsSelection ? (
              <MotionTrackSelectionPanel
                tracks={availableTracks}
                videoUrl={videoPlaybackUrl}
                persistedSelectedTrackId={selectedTrackId}
                submitting={trackSubmitting}
                onConfirm={runTrackSelection}
              />
            ) : showResultPreview && (resultPreview?.frames?.length || videoPlaybackUrl) ? (
              <MotionSkeletonPreview
                frames={resultPreview?.frames || []}
                width={previewWidth}
                height={resultHeight - 8}
                fps={resultPreview?.preview_fps || 12}
                coordinateSpace={resultPreview?.coordinate_space}
                playing={playing}
                onPlayingChange={setPlaying}
                frameIndex={frameIdx}
                onFrameIndexChange={setFrameIdx}
                videoUrl={videoPlaybackUrl}
                videoAlignedFrames={videoAlignedPreview?.frames || []}
                isNodeDragging={nodeData.isNodeDragging}
              />
            ) : isBusy ? (
              <div className="pcv-gen-result-loading" data-testid="motion-generating-empty">
                <LoadingOutlined spin />
                <span>{t('motionReconstruction.preview.generating')}</span>
              </div>
            ) : (
              <div className="pcv-gen-result-empty" data-testid="motion-result-empty">
                <span className="pcv-gen-result-empty-icon">{def.icon}</span>
                <span>{t('motionReconstruction.preview.emptyTitle', { defaultValue: '暂无动作重建结果' })}</span>
                <span className="pcv-gen-result-empty-hint">
                  {showExpanded
                    ? t('motionReconstruction.preview.emptyHintExpanded', {
                        defaultValue: '请添加视频输入，配置参数后点击「生成结果」',
                      })
                    : t('motionReconstruction.preview.emptyHintCollapsed', {
                        defaultValue: '展开卡片添加视频输入，再点击「生成结果」',
                      })}
                </span>
              </div>
            )}

            {isBusy && showResultPreview ? (
              <div className="pcv-motion-result-overlay" data-testid="motion-generating-overlay">
                <LoadingOutlined spin />
                <span>{statusLabel}</span>
              </div>
            ) : null}
          </div>

          {failedDisplayMessage ? (
            <div className="pcv-gen-capability-warning pcv-gen-capability-warning--error">{failedDisplayMessage}</div>
          ) : null}
        </div>

        {showExpanded ? (
          <div
            className="pcv-brief-body pcv-gen-input-body nodrag"
            onClick={(e) => e.stopPropagation()}
            data-testid="motion-expanded-body"
          >
            {/* ③ Input Assets — video only (backend contract) */}
            <div className="pcv-brief-section" data-testid="motion-input-assets">
              <span className="pcv-brief-section-label">
                {t('motionReconstruction.assets.label', { defaultValue: '输入素材' })}
              </span>
              <div className="pcv-motion-asset-card" data-testid="motion-video-slot">
                <span className="pcv-gen-field-label">{t('motionReconstruction.video.label')}</span>
                <div className="pcv-gen-field-chip">
                  <VideoCameraOutlined />
                  <span className="pcv-gen-field-chip-name">{videoLabel}</span>
                  <button type="button" className="pcv-gen-link-btn" disabled={isBusy} onClick={openVideoPicker}>
                    {inputVideoAssetId
                      ? t('motionReconstruction.video.replace', { defaultValue: '更换视频' })
                      : t('motionReconstruction.video.select')}
                  </button>
                </div>
                <span className="pcv-motion-input-mode-hint" data-testid="motion-input-mode">
                  {t('motionReconstruction.inputMode.videoOnly', {
                    defaultValue: '当前支持：视频输入 → 动作提取 / 重建',
                  })}
                </span>
              </div>
            </div>

            {/* ④ Parameters — real contract only */}
            <div className="pcv-brief-section" data-testid="motion-parameters">
              <span className="pcv-brief-section-label">
                {t('motionReconstruction.params.label', { defaultValue: '生成参数' })}
              </span>
              <div className="pcv-gen-params-grid pcv-motion-params-grid">
                <div className="pcv-gen-field-row">
                  <span className="pcv-gen-field-label">
                    {t('motionReconstruction.params.quality', { defaultValue: '质量模式' })}
                  </span>
                  <CanvasSelect
                    size="small"
                    className="pcv-gen-field-select nodrag"
                    popupClassName="pcv-gen-field-select-popup"
                    data-testid="motion-quality-select"
                    value={qualityMode}
                    disabled={isBusy}
                    options={[
                      { value: 'fast', label: t('virtualTryOn.quality.fast', { defaultValue: '快速' }) },
                      { value: 'balanced', label: t('virtualTryOn.quality.balanced', { defaultValue: '平衡' }) },
                    ]}
                    onChange={(v) => patchFields(markStaleIfHasResult({ qualityMode: v }))}
                    popupMatchSelectWidth={false}
                    getPopupContainer={() => document.body}
                  />
                </div>
                {availableTracks.length > 1 || selectedTrackId ? (
                  <div className="pcv-gen-field-row">
                    <span className="pcv-gen-field-label">
                      {t('motionReconstruction.trackSelection.targetPerson', { defaultValue: '目标人物' })}
                    </span>
                    <CanvasSelect
                      size="small"
                      className="pcv-gen-field-select nodrag"
                      popupClassName="pcv-gen-field-select-popup"
                      data-testid="motion-track-select"
                      value={selectedTrackId || undefined}
                      disabled={isBusy || availableTracks.length === 0}
                      placeholder={t('motionReconstruction.trackSelection.title')}
                      options={availableTracks.map((tr, idx) => ({
                        value: tr.track_id,
                        label: t('motionReconstruction.trackSelection.personLabel', { index: idx + 1 }),
                      }))}
                      onChange={(v) => {
                        if (status === 'SELECTION_REQUIRED') {
                          void runTrackSelection(v)
                        } else {
                          patchFields(markStaleIfHasResult({ selectedTrackId: v }))
                        }
                      }}
                      popupMatchSelectWidth={false}
                      getPopupContainer={() => document.body}
                    />
                  </div>
                ) : null}
                <div className="pcv-gen-field-row">
                  <span className="pcv-gen-field-label">
                    {t('motionReconstruction.params.outputType', { defaultValue: '输出类型' })}
                  </span>
                  <span className="pcv-gen-field-chip-name" data-testid="motion-output-type-fixed">
                    {t('motionReconstruction.params.outputTypeFixed', {
                      defaultValue: '3D Motion Asset（骨架）',
                    })}
                  </span>
                </div>
              </div>
            </div>

            {/* ⑤ Prompt — last input section */}
            <div className="pcv-brief-section" data-testid="motion-prompt-section">
              <span className="pcv-brief-section-label">
                {t('motionReconstruction.prompt.label', { defaultValue: '提示词（可选）' })}
              </span>
              <textarea
                className="pcv-brief-textarea nodrag"
                data-testid="motion-prompt"
                placeholder={t('motionReconstruction.prompt.placeholder', {
                  defaultValue: '提取人物完整动作，保持动作连续，忽略背景人物，减少关键点抖动……',
                })}
                value={prompt}
                maxLength={PROMPT_MAX_LENGTH}
                onChange={(e) => patchFields(markStaleIfHasResult({ prompt: e.target.value }))}
              />
              <span className="pcv-brief-textarea-counter">
                {prompt.length}/{PROMPT_MAX_LENGTH}
              </span>
            </div>

            {/* ⑥ Footer — Provider display | Skill | Generate (no Diverge) */}
            <div className="pcv-gen-footer nodrag" onClick={(e) => e.stopPropagation()} data-testid="motion-footer">
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                data-testid="motion-provider-select"
                value="cloud_motion"
                disabled
                options={[
                  {
                    value: 'cloud_motion',
                    label: t('motionReconstruction.provider.cloud', { defaultValue: '云端 · Motion' }),
                  },
                ]}
                popupMatchSelectWidth={false}
                getPopupContainer={() => document.body}
              />
              <CanvasSelect
                size="small"
                className="pcv-gen-field-select pcv-gen-footer-select nodrag"
                popupClassName="pcv-gen-field-select-popup"
                data-testid="motion-skill-select"
                value={skillSelectionSource === 'EXPLICIT' ? MOTION_SKILL_KEY : 'AUTO'}
                options={[
                  { value: 'AUTO', label: t('motionReconstruction.skill.auto', { defaultValue: '自动（推荐）' }) },
                  {
                    value: MOTION_SKILL_KEY,
                    label: t('motionReconstruction.skill.v1', { defaultValue: '动作重建 v1' }),
                  },
                ]}
                onChange={(v) =>
                  patchFields(
                    markStaleIfHasResult(
                      v === 'AUTO'
                        ? { skillSelectionSource: 'AUTO', skillKey: null }
                        : { skillSelectionSource: 'EXPLICIT', skillKey: v, skillVersion: MOTION_SKILL_VERSION },
                    ),
                  )
                }
                popupMatchSelectWidth={false}
                getPopupContainer={() => document.body}
              />
              <button
                type="button"
                className="pcv-gen-footer-generate-btn"
                data-testid="motion-generate-btn"
                disabled={!canGenerate}
                onClick={handleGenerateClick}
              >
                {isBusy ? <LoadingOutlined spin /> : <ThunderboltOutlined />}{' '}
                {isBusy
                  ? t('motionReconstruction.actions.generating')
                  : t('motionReconstruction.actions.generateResult', { defaultValue: '生成结果' })}
              </button>
            </div>
          </div>
        ) : null}
      </div>

      {showExternalQuickActions ? (
        <MotionResultWorkflowRail
          className="pcv-media-workflow-toolbar--motion-reconstruction"
          onMouseEnter={activateResultZone}
          onMouseLeave={deactivateResultZone}
          onQuickCreateGenerateVideo={() => nodeData.onQuickCreateGenerateVideoFromMotion?.(id)}
        />
      ) : null}

      <CanvasActionPopover
        anchorEl={historyBtnEl}
        open={historyOpen}
        onClose={() => setHistoryOpen(false)}
        title={t('generation.historyVersions', { defaultValue: '历史版本' })}
      >
        <div className="pcv-motion-history-list" data-testid="motion-history-panel">
          {outputMotionAssetId ? (
            <div className="pcv-motion-history-item is-current">
              <UserSwitchOutlined />
              <div>
                <div>{t('motionReconstruction.history.current', { defaultValue: '当前结果' })}</div>
                <div className="pcv-motion-history-meta">{outputMotionAssetId}</div>
              </div>
            </div>
          ) : (
            <div className="pcv-gen-result-empty-hint">
              {t('motionReconstruction.history.empty', { defaultValue: '暂无历史版本' })}
            </div>
          )}
          {previousOutputMotionAssetId ? (
            <div className="pcv-motion-history-item">
              <div>
                <div>{t('motionReconstruction.history.previous', { defaultValue: '上一版本' })}</div>
                <div className="pcv-motion-history-meta">{previousOutputMotionAssetId}</div>
              </div>
            </div>
          ) : null}
        </div>
      </CanvasActionPopover>
    </div>
  )
}

export default memo(MotionReconstructionNodeImpl)
